/**
 * The dashboard's date range, and the one rule that makes it correct.
 *
 * THE ANCHOR IS THE SERVER'S DAY, NEVER THE BROWSER'S.
 *
 * `queries.js` explains why the default asks for no dates at all: at 23:40 IST on
 * 31 March a browser in any other timezone works out a different "today", asks
 * for 1 April, and disagrees with the invoice numbers the shop drew that evening.
 * The database answers that question and echoes the day back.
 *
 * A range picker could have thrown that away — "last 7 days" computed from
 * `new Date()` is the same bug wearing a different hat, and it would be wrong for
 * seven days at a time instead of forty minutes. So every preset here is derived
 * from `anchor`, which is the day the *server* named. Until that answer arrives
 * there is no range but TODAY, and TODAY is the one preset that needs no
 * arithmetic because it sends nothing.
 *
 * Dates are handled as 'YYYY-MM-DD' strings and stepped through UTC noon. Not
 * midnight: a Date at midnight in a timezone behind UTC lands on the previous
 * day the moment it is formatted back, which is how a "last 7 days" quietly
 * becomes eight.
 */

export const TODAY = 'today'
export const YESTERDAY = 'yesterday'
export const LAST_7 = 'last_7'
export const LAST_30 = 'last_30'
export const THIS_MONTH = 'this_month'
export const LAST_MONTH = 'last_month'
export const CUSTOM = 'custom'

/** The order they appear in the menu, and the label each carries. */
export const PRESETS = [
  { id: TODAY, label: 'Today' },
  { id: YESTERDAY, label: 'Yesterday' },
  { id: LAST_7, label: 'Last 7 days' },
  { id: LAST_30, label: 'Last 30 days' },
  { id: THIS_MONTH, label: 'This month' },
  { id: LAST_MONTH, label: 'Last month' },
  { id: CUSTOM, label: 'Custom…' },
]

const DAY_MS = 24 * 60 * 60 * 1000

/** 'YYYY-MM-DD' -> a Date pinned to UTC noon. See the note above on midnight. */
function parse(iso) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d, 12))
}

function format(date) {
  return date.toISOString().slice(0, 10)
}

function addDays(iso, days) {
  return format(new Date(parse(iso).getTime() + days * DAY_MS))
}

function firstOfMonth(iso) {
  const d = parse(iso)
  return format(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 12)))
}

function lastOfPreviousMonth(iso) {
  return addDays(firstOfMonth(iso), -1)
}

/**
 * The two dates a preset means, given the day the server named.
 *
 * Returns `null` for TODAY — deliberately, and every caller must handle it by
 * sending no dates rather than substituting the anchor. The distinction matters
 * on the one evening of the year when it matters: sending nothing asks the
 * database what today is *at the moment the query runs*; sending the anchor asks
 * for a day that may already be yesterday by the time a shop still billing at
 * ten past midnight presses post.
 */
export function resolve(preset, anchor, custom) {
  if (preset === TODAY) return null
  if (!anchor) return null

  switch (preset) {
    case YESTERDAY: {
      const d = addDays(anchor, -1)
      return { date_from: d, date_to: d }
    }
    // Inclusive of today, so "last 7 days" is a week ending now — which is what
    // a shopkeeper means by it, not seven days ending yesterday.
    case LAST_7:
      return { date_from: addDays(anchor, -6), date_to: anchor }
    case LAST_30:
      return { date_from: addDays(anchor, -29), date_to: anchor }
    case THIS_MONTH:
      return { date_from: firstOfMonth(anchor), date_to: anchor }
    case LAST_MONTH: {
      const end = lastOfPreviousMonth(anchor)
      return { date_from: firstOfMonth(end), date_to: end }
    }
    case CUSTOM:
      if (!custom?.date_from || !custom?.date_to) return null
      // Entered backwards is a slip, not an error worth a message: a range that
      // reads 20th to 12th means the 12th to the 20th to everyone except a
      // BETWEEN clause, which returns nothing and looks like a shop with no
      // sales.
      return custom.date_from <= custom.date_to
        ? { date_from: custom.date_from, date_to: custom.date_to }
        : { date_from: custom.date_to, date_to: custom.date_from }
    default:
      return null
  }
}

/** Is this range a single day? Decides "on this day" versus "over this period". */
export function isOneDay(dates) {
  return dates === null || dates.date_from === dates.date_to
}

/**
 * What to call the period in a sentence, e.g. "Sales today", "Sales this month".
 *
 * Tile labels read better as a phrase than as a date, and a phrase also survives
 * the tile being narrow. The exact dates are always on the picker itself, so
 * nothing is hidden by shortening them here.
 */
export function periodPhrase(preset) {
  switch (preset) {
    case TODAY: return 'today'
    case YESTERDAY: return 'yesterday'
    case LAST_7: return 'in 7 days'
    case LAST_30: return 'in 30 days'
    case THIS_MONTH: return 'this month'
    case LAST_MONTH: return 'last month'
    default: return 'in this period'
  }
}

/** The picker's own caption: the dates, spelled out, or the word for today. */
export function describe(preset, dates, formatDate) {
  if (preset === TODAY || dates === null) return null
  if (dates.date_from === dates.date_to) return formatDate(dates.date_from)
  return `${formatDate(dates.date_from)} — ${formatDate(dates.date_to)}`
}
